// 多语言配置文件

export type Language = 'zh' | 'en'

export interface Translations {
  // Header
  title: string
  subtitle: string
  connectWallet: string
  disconnect: string
  wrongNetwork: string
  testMode: string

  // Tabs
  blurToken: string
  usdt: string

  // Balance
  ethBalance: string
  blurBalance: string
  usdtBalance: string
  cUsdtBalance: string
  encrypted: string

  // Buttons
  generateNewKeys: string
  approve: string
  approved: string
  alreadyApproved: string
  deposit: string
  withdraw: string
  wrap: string
  unwrap: string
  max: string
  redownloadKeys: string
  processing: string
  switchNetwork: string

  // Labels
  selectPool: string
  amount: string
  secret: string
  nullifier: string
  commitment: string
  recipientAddress: string
  anonymitySet: string
  deposits: string
  privacyStrong: string
  privacyWeak: string
  waitForMore: string
  fixedAmount: string

  // Pool descriptions
  poolSmall: string
  poolRecommended: string
  poolLarge: string

  // Messages
  keysGenerated: string
  saveKeys: string
  depositSuccess: string
  withdrawSuccess: string
  withdrawSuccessPoints: string
  pointsEarned: string
  completePrivacySystem: string
  pointsUpdated: string
  wrapSuccess: string
  unwrapSuccess: string
  approveSuccess: string

  // Warnings
  warningKeep: string
  warningWithdrawTime: string
  warningNoRecovery: string

  // Errors
  errorConnectWallet: string
  errorGenerateKeys: string
  errorEnterAmount: string
  errorApproveFirst: string
  errorInvalidAmount: string
  errorInsufficientBalance: string

  // Status messages
  statusConnected: string
  statusConnectedTo: string
  statusSwitchToSepolia: string
  statusContractCallFailed: string
  statusNetworkError: string

  // Info
  infoHowItWorks: string
  infoPrivacyFlow: string
  infoStep1: string
  infoStep2: string
  infoStep3: string
  infoStep4: string

  // Modal
  modalConfirm: string
  modalCancel: string
  modalDetails: string

  // Modal titles
  modalTitleGenerateKeys: string
  modalTitleApprove: string
  modalTitleWrongAmount: string
  modalTitleConnectWallet: string
  modalTitleEnterKeys: string
  modalTitleEnterAddress: string
  modalTitleEnterAmount: string

  // Modal messages
  modalMsgGenerateKeys: string
  modalMsgApprove: string
  modalMsgWrongAmount: string
  modalMsgConnectWallet: string
  modalMsgEnterKeys: string
  modalMsgEnterAddress: string
  modalMsgEnterAmount: string

  // Friendly errors
  errorTxCancelled: string
  errorTxCancelledMsg: string
  errorInsufficientFunds: string
  errorInsufficientFundsMsg: string
  errorGasInsufficient: string
  errorGasInsufficientMsg: string
  errorNotApproved: string
  errorNotApprovedMsg: string
  errorCommitmentUsed: string
  errorCommitmentUsedMsg: string
  errorNullifierUsed: string
  errorNullifierUsedMsg: string
  errorTooEarly: string
  errorTooEarlyMsg: string
  errorNetwork: string
  errorNetworkMsg: string
  errorTxFailed: string
  errorTxFailedMsg: string
  errorInvalidAmountTitle: string
  errorInvalidAmountMsg: string
  errorDefault: string
  errorDefaultMsg: string
}

export const translations: Record<Language, Translations> = {
  zh: {
    // Header
    title: 'Nullayer',
    subtitle: '零知识隐私保护协议',
    connectWallet: '连接钱包',
    disconnect: '断开连接',
    wrongNetwork: '错误的网络',
    testMode: '测试模式',

    // Tabs
    blurToken: 'BLUR Token',
    usdt: 'USDT',

    // Balance
    ethBalance: 'ETH 余额',
    blurBalance: 'BLUR 余额',
    usdtBalance: 'USDT 余额',
    cUsdtBalance: 'cUSDT 余额',
    encrypted: '已加密',

    // Buttons
    generateNewKeys: '生成新密钥',
    approve: '授权',
    approved: '已授权',
    alreadyApproved: 'cUSDT 已授权',
    deposit: '存款',
    withdraw: '取款',
    wrap: 'Wrap USDT → cUSDT',
    unwrap: 'Unwrap cUSDT → USDT',
    max: 'MAX',
    redownloadKeys: '重新下载密钥文件',
    processing: '处理中...',
    switchNetwork: '切换网络',

    // Labels
    selectPool: '选择金额池',
    amount: '金额',
    secret: 'Secret',
    nullifier: 'Nullifier',
    commitment: 'Commitment',
    recipientAddress: '接收地址',
    anonymitySet: '匿名集大小',
    deposits: '个存款',
    privacyStrong: '隐私强',
    privacyWeak: '建议等待更多用户',
    waitForMore: '建议等待更多用户',
    fixedAmount: '固定金额',

    // Pool descriptions
    poolSmall: '小额测试',
    poolRecommended: '推荐使用',
    poolLarge: '大额交易',

    // Messages
    keysGenerated: '密钥已生成并已下载到本地！请妥善保管文件',
    saveKeys: '请务必保存 Secret 和 Nullifier！取款时需要',
    depositSuccess: '存款成功！',
    withdrawSuccess: '取款成功！cUSDT 已发送到',
    withdrawSuccessPoints: '取款成功！获得',
    pointsEarned: '积分',
    completePrivacySystem: '完整隐私系统 ↓⭐',
    pointsUpdated: '积分已更新',
    wrapSuccess: 'Wrap 成功！',
    unwrapSuccess: 'Unwrap 成功！',
    approveSuccess: 'cUSDT 授权成功（无限额度）！',

    // Warnings
    warningKeep: '请务必保存！',
    warningWithdrawTime: '存款后需要等待 10 秒才能取款',
    warningNoRecovery: '如果丢失此文件，将无法取款',

    // Errors
    errorConnectWallet: '请先连接钱包',
    errorGenerateKeys: '请先生成密钥',
    errorEnterAmount: '请输入金额',
    errorApproveFirst: '请先授权',
    errorInvalidAmount: '金额无效',
    errorInsufficientBalance: '余额不足',

    // Status messages
    statusConnected: '✨ 已连接',
    statusConnectedTo: '✨ 已连接到',
    statusSwitchToSepolia: '⚠️ 请切换到 Sepolia 测试网',
    statusContractCallFailed: '❌ 合约调用失败，请确保已连接到 Sepolia 测试网',
    statusNetworkError: '❌ 网络连接问题，请检查 RPC 连接',

    // Info
    infoHowItWorks: '工作原理',
    infoPrivacyFlow: '完整隐私流程',
    infoStep1: '地址 A: Wrap USDT → cUSDT → 存入隐私盾',
    infoStep2: '等待 10 秒',
    infoStep3: '地址 B: 取出 cUSDT → Unwrap → 获得 USDT',
    infoStep4: '链上：地址 A 和地址 B 之间没有关联！',

    // Modal
    modalConfirm: '确定',
    modalCancel: '取消',
    modalDetails: '详细信息：',

    // Modal titles
    modalTitleGenerateKeys: '请先生成密钥',
    modalTitleApprove: '请先授权',
    modalTitleWrongAmount: '金额不正确',
    modalTitleConnectWallet: '请连接钱包',
    modalTitleEnterKeys: '请输入密钥',
    modalTitleEnterAddress: '请输入接收地址',
    modalTitleEnterAmount: '请输入金额',

    // Modal messages
    modalMsgGenerateKeys: '存款前需要生成密钥（Secret 和 Nullifier）。点击"生成新密钥"按钮生成密钥。',
    modalMsgApprove: '存款前需要先授权 cUSDT 给隐私盾合约。请点击"授权 cUSDT"按钮进行授权。',
    modalMsgWrongAmount: '当前金额池只接受 {amount} cUSDT 的固定金额，请输入正确的金额或点击 MAX 按钮。',
    modalMsgConnectWallet: '请先连接您的钱包后再进行操作。',
    modalMsgEnterKeys: '取款需要提供存款时生成的 Secret 和 Nullifier。如果您有密钥文件，请打开文件并复制密钥。',
    modalMsgEnterAddress: '请输入接收 cUSDT 的钱包地址。',
    modalMsgEnterAmount: '请输入有效的金额。',

    // Friendly errors
    errorTxCancelled: '交易已取消',
    errorTxCancelledMsg: '您已取消了这笔交易。',
    errorInsufficientFunds: '余额不足',
    errorInsufficientFundsMsg: '您的账户余额不足以完成此交易。',
    errorGasInsufficient: 'Gas 费用不足',
    errorGasInsufficientMsg: '您的 ETH 余额不足以支付 Gas 费用。',
    errorNotApproved: '未授权',
    errorNotApprovedMsg: '请先授权代币后再进行操作。',
    errorCommitmentUsed: '密钥已被使用',
    errorCommitmentUsedMsg: '此密钥已经被用于存款，请生成新的密钥。',
    errorNullifierUsed: '已取款',
    errorNullifierUsedMsg: '此密钥已经被用于取款，无法重复取款。',
    errorTooEarly: '请稍后再试',
    errorTooEarlyMsg: '存款后需要等待 10 秒才能取款，请稍后再试。',
    errorNetwork: '网络连接错误',
    errorNetworkMsg: '请检查您的网络连接后重试。',
    errorTxFailed: '交易失败',
    errorTxFailedMsg: '合约执行失败，请检查交易参数是否正确。',
    errorInvalidAmountTitle: '金额无效',
    errorInvalidAmountMsg: '请输入有效的金额。',
    errorDefault: '操作失败',
    errorDefaultMsg: '操作失败，请重试。',
  },

  en: {
    // Header
    title: 'Nullayer',
    subtitle: 'Zero-Knowledge Privacy Shield',
    connectWallet: 'Connect Wallet',
    disconnect: 'Disconnect',
    wrongNetwork: 'Wrong Network',
    testMode: 'Test Mode',

    // Tabs
    blurToken: 'BLUR Token',
    usdt: 'USDT',

    // Balance
    ethBalance: 'ETH Balance',
    blurBalance: 'BLUR Balance',
    usdtBalance: 'USDT Balance',
    cUsdtBalance: 'cUSDT Balance',
    encrypted: 'Encrypted',

    // Buttons
    generateNewKeys: 'Generate New Keys',
    approve: 'Approve',
    approved: 'Approved',
    alreadyApproved: 'cUSDT Already Approved',
    deposit: 'Deposit',
    withdraw: 'Withdraw',
    wrap: 'Wrap USDT → cUSDT',
    unwrap: 'Unwrap cUSDT → USDT',
    max: 'MAX',
    redownloadKeys: 'Re-download Keys',
    processing: 'Processing...',
    switchNetwork: 'Switch Network',

    // Labels
    selectPool: 'Select Pool',
    amount: 'Amount',
    secret: 'Secret',
    nullifier: 'Nullifier',
    commitment: 'Commitment',
    recipientAddress: 'Recipient Address',
    anonymitySet: 'Anonymity Set',
    deposits: 'deposits',
    privacyStrong: 'Privacy Strong',
    privacyWeak: 'Wait for more users',
    waitForMore: 'Wait for more users',
    fixedAmount: 'Fixed Amount',

    // Pool descriptions
    poolSmall: 'Small Test',
    poolRecommended: 'Recommended',
    poolLarge: 'Large Amount',

    // Messages
    keysGenerated: 'Keys generated and downloaded! Please keep the file safe',
    saveKeys: 'Save these keys! You\'ll need them to withdraw',
    depositSuccess: 'Deposit successful!',
    withdrawSuccess: 'Withdrawal successful! cUSDT sent to',
    withdrawSuccessPoints: 'Withdrawal successful! Earned',
    pointsEarned: 'points',
    completePrivacySystem: 'Complete Privacy System ↓⭐',
    pointsUpdated: 'Points updated',
    wrapSuccess: 'Wrap successful!',
    unwrapSuccess: 'Unwrap successful!',
    approveSuccess: 'cUSDT approved successfully (unlimited)!',

    // Warnings
    warningKeep: 'Keep safe!',
    warningWithdrawTime: 'Wait 10 seconds after deposit before withdrawal',
    warningNoRecovery: 'If you lose this file, you cannot withdraw',

    // Errors
    errorConnectWallet: 'Please connect wallet',
    errorGenerateKeys: 'Please generate keys first',
    errorEnterAmount: 'Please enter amount',
    errorApproveFirst: 'Please approve first',
    errorInvalidAmount: 'Invalid amount',
    errorInsufficientBalance: 'Insufficient balance',

    // Status messages
    statusConnected: '✨ Connected',
    statusConnectedTo: '✨ Connected to',
    statusSwitchToSepolia: '⚠️ Please switch to Sepolia testnet',
    statusContractCallFailed: '❌ Contract call failed, please ensure connected to Sepolia testnet',
    statusNetworkError: '❌ Network connection issue, please check RPC connection',

    // Info
    infoHowItWorks: 'How It Works',
    infoPrivacyFlow: 'Complete Privacy Flow',
    infoStep1: 'Address A: Wrap USDT → cUSDT → Deposit to Shield',
    infoStep2: 'Wait 10 seconds',
    infoStep3: 'Address B: Withdraw cUSDT → Unwrap → Get USDT',
    infoStep4: 'On-chain: No link between Address A and Address B!',

    // Modal
    modalConfirm: 'OK',
    modalCancel: 'Cancel',
    modalDetails: 'Details:',

    // Modal titles
    modalTitleGenerateKeys: 'Generate Keys First',
    modalTitleApprove: 'Approve First',
    modalTitleWrongAmount: 'Wrong Amount',
    modalTitleConnectWallet: 'Connect Wallet',
    modalTitleEnterKeys: 'Enter Keys',
    modalTitleEnterAddress: 'Enter Address',
    modalTitleEnterAmount: 'Enter Amount',

    // Modal messages
    modalMsgGenerateKeys: 'You need to generate keys (Secret and Nullifier) before deposit. Click "Generate New Keys" button.',
    modalMsgApprove: 'You need to approve cUSDT to the Shield contract before deposit. Click "Approve cUSDT" button.',
    modalMsgWrongAmount: 'This pool only accepts fixed amount of {amount} cUSDT. Please enter the correct amount or click MAX button.',
    modalMsgConnectWallet: 'Please connect your wallet first.',
    modalMsgEnterKeys: 'You need to provide Secret and Nullifier from your deposit. If you have the key file, please open it and copy the keys.',
    modalMsgEnterAddress: 'Please enter the wallet address to receive cUSDT.',
    modalMsgEnterAmount: 'Please enter a valid amount.',

    // Friendly errors
    errorTxCancelled: 'Transaction Cancelled',
    errorTxCancelledMsg: 'You cancelled the transaction.',
    errorInsufficientFunds: 'Insufficient Funds',
    errorInsufficientFundsMsg: 'Your account balance is insufficient for this transaction.',
    errorGasInsufficient: 'Insufficient Gas',
    errorGasInsufficientMsg: 'Your ETH balance is insufficient to pay for gas fees.',
    errorNotApproved: 'Not Approved',
    errorNotApprovedMsg: 'Please approve the token first.',
    errorCommitmentUsed: 'Keys Already Used',
    errorCommitmentUsedMsg: 'These keys have already been used for deposit. Please generate new keys.',
    errorNullifierUsed: 'Already Withdrawn',
    errorNullifierUsedMsg: 'These keys have already been used for withdrawal. Cannot withdraw twice.',
    errorTooEarly: 'Please Wait',
    errorTooEarlyMsg: 'You need to wait 10 seconds after deposit before withdrawal. Please try again later.',
    errorNetwork: 'Network Error',
    errorNetworkMsg: 'Please check your network connection and try again.',
    errorTxFailed: 'Transaction Failed',
    errorTxFailedMsg: 'Contract execution failed. Please check transaction parameters.',
    errorInvalidAmountTitle: 'Invalid Amount',
    errorInvalidAmountMsg: 'Please enter a valid amount.',
    errorDefault: 'Operation Failed',
    errorDefaultMsg: 'Operation failed. Please try again.',
  },
}

// 获取浏览器语言
export const getBrowserLanguage = (): Language => {
  const browserLang = navigator.language.toLowerCase()
  if (browserLang.startsWith('zh')) {
    return 'zh'
  }
  return 'en'
}

// 从 localStorage 获取保存的语言，如果没有则使用浏览器语言
export const getSavedLanguage = (): Language => {
  const saved = localStorage.getItem('language')
  if (saved === 'zh' || saved === 'en') {
    return saved
  }
  return getBrowserLanguage()
}

// 保存语言到 localStorage
export const saveLanguage = (lang: Language) => {
  localStorage.setItem('language', lang)
}
